import React from 'react';
import { i18n } from 'config/locales';
import ConnectedAlternateAddress from 'components/Form/Location/AlternateAddress';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import PVQName from 'components/Form/PVQName/Name';
import DateControl from 'components/Form/DateControl';
import Show from 'components/Form/Show';
import ForeignBornDocuments from 'components/Form/ForeignBornDocuments';
import BranchCollection from 'components/Form/BranchCollection';
import Country from 'components/Form/Country';
import NotApplicable from 'components/Form/NotApplicable';
import Telephone from 'components/Form/Telephone';
import Email from 'components/Form/Email';
import Branch from 'components/Form/Branch';
import Location from 'components/Form/Location';
import SSN from 'components/Form/SSN';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError,
  CommonOnUpdate,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { RelationshipsCivilUnionModel } from 'forms/sections/relationships/marital/types';
import { extractDate } from 'components/Section/History/dateranges';
import { DateModel } from 'forms/shared/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import ForeignBornDocumentsCivil from 'components/Form/ForeignBornDocumentsCivil';
import Text from 'components/Form/Text';
import PVQEmail from 'components/Form/PVQEmail';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { countryString } from '../../../../validators/location';
import { pickDate } from '../../../../validators/helpers';
import OtherName from './OtherName';

interface Props extends Partial<RelationshipsCivilUnionModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  required?: CommonRequired
  requireRelationshipMaritalForeignBornDocExpiration?: boolean
  currentAddress?: any
  onSpouseUpdate?: any
  applicantBirthdate?: DateModel
  dispatch?: Dispatch
  divorcedComment?: Function
  attribute?: string
  formType?: string
}

class CivilUnion extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<RelationshipsCivilUnionModel>) => {
    this.props.onUpdate({
      Name: this.props.Name,
      Birthdate: this.props.Birthdate,
      BirthPlace: this.props.BirthPlace,
      ForeignBornDocument: this.props.ForeignBornDocument,
      ForeignBornDocumentCivil: this.props.ForeignBornDocumentCivil, // PVQ only
      SSN: this.props.SSN,
      OtherNames: this.props.OtherNames,
      Citizenship: this.props.Citizenship,
      EnteredCivilUnion: this.props.EnteredCivilUnion,
      Address: this.props.Address,
      SameAddress: this.props.SameAddress, // PVQ only
      SpouseAddress: this.props.SpouseAddress, // PVQ only
      GovOffice: this.props.GovOffice, // PVQ only
      Location: this.props.Location,
      Telephone: this.props.Telephone,
      AlternateAddress: this.props.AlternateAddress,
      Email: this.props.Email,
      EmailNotApplicable: this.props.EmailNotApplicable,
      Separated: this.props.Separated,
      DateSeparated: this.props.DateSeparated,
      AddressSeparated: this.props.AddressSeparated,
      AddressSeparatedNotApplicable: this.props.AddressSeparatedNotApplicable,
      Divorced: this.props.Divorced,
      UseApplicantPhoneNotApplicable: this.props.UseApplicantPhoneNotApplicable,
      UseCurrentAddress: this.props.UseCurrentAddress,
      Comments: this.props.Comments,
      ContinueComments: this.props.ContinueComments,
      SeparatedComments: this.props.SeparatedComments,
      SeparationComments: this.props.SeparationComments,
      AnswerComments: this.props.AnswerComments,
      AddressComments: this.props.AddressComments,
      PhysicalLocationComments: this.props.PhysicalLocationComments,
      addressBooks: this.props.addressBooks,
      ...queue,
    });
  };

  updateName = (values: RelationshipsCivilUnionModel['Name']) => {
    this.update({
      Name: values,
    });

    if (this.props.onSpouseUpdate) {
      this.props.onSpouseUpdate(values);
    }
  };

  updateUseCurrentAddress = (values: RelationshipsCivilUnionModel['UseCurrentAddress']) => {
    const nextValues = { ...values, applicable: !values.applicable };

    this.update({
      UseCurrentAddress: nextValues,
      Address: values.applicable !== false ? { ...this.props.currentAddress } : {},
    });
  };

  updateUseApplicantPhoneNotApplicable = (
    values: RelationshipsCivilUnionModel['UseApplicantPhoneNotApplicable']
  ) => {
    this.update({
      Telephone: values.applicable ? this.props.Telephone : {},
      UseApplicantPhoneNotApplicable: values,
    });
  };

  updateComments = (values: RelationshipsCivilUnionModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateContinueComments = (values: RelationshipsCivilUnionModel['ContinueComments']) => {
    this.update({
      ContinueComments: values,
    });
  };

  updateSeparatedComments = (values: RelationshipsCivilUnionModel['SeparatedComments']) => {
    this.update({
      SeparatedComments: values,
    });
  };

  updateSeparationComments = (values: RelationshipsCivilUnionModel['SeparationComments']) => {
    this.update({
      SeparationComments: values,
    });
  };

  updateBirthPlace = (values: RelationshipsCivilUnionModel['BirthPlace']) => {
    const additional = values?.country?.value === 'United States' ? { ForeignBornDocument: {} } : {};
    this.update({
      BirthPlace: values,
      ...additional
    });
  };

  updateForeignBornDocument = (values: RelationshipsCivilUnionModel['ForeignBornDocument']) => {
    this.update({
      ForeignBornDocument: values,
    });
  };

  updateForeignBornDocumentCivil = (values: RelationshipsCivilUnionModel['ForeignBornDocumentCivil']) => {
    this.update({
      ForeignBornDocumentCivil: values,
    });
  };

  updateSameAddress = (values: RelationshipsCivilUnionModel['SameAddress']) => {
    this.update({
      SameAddress: values,
    });
  };

  updateGovOffice = (values: RelationshipsCivilUnionModel['GovOffice']) => {
    this.update({
      GovOffice: values,
    });
  };

  updateDivorced = (values: RelationshipsCivilUnionModel['Divorced']) => {
    this.update({
      Divorced: values
    });
  };

  componentDidMount() {
    if (this.props.UseCurrentAddress?.applicable) {
      this.update({
        Address: { ...this.props.currentAddress },
      });
    }
  }

  render() {
    /**
       * `this.props.BirthPlace` can end up in a number of different states,
       * depending on the choices a user makes in this section _and_ on
       * the initial state of the app.
       *
       * The initial, pristine value for this.props.BirthPlace is `undefined`.
       * This occurs despite the BirthPlace key/value pair in defaultProps,
       * indicating that the value is being explicitly supplied at some point.
       *
       * Once either the 'US' or 'outside the US' options have been selected, the value
       * of `BirthPlace` changes to one of these data structures on page load:
       *
       * {layout: "Birthplace", country: "United States"}
       *
       * OR
       *
       * {layout: "Birthplace", city: "Hamburg", country: "Germany"}
       *
       * If the user has indicated their spouse has an international birthplace, and then
       * clicks on 'US', `BirthPlace` will get updated to the following data structure:
       *
       * {value: "United States"}
       *
       * If the user then clicks on 'outside the US', and fills in any of the fields in
       * the displayd form, the `BirthPlace` prop is resupplied to the component with
       * the following structure:
       *
       *  {name: "country", comments: "", showComments: false, value: ['germany']}
       *
       */
    const { requireRelationshipMaritalForeignBornDocExpiration, attribute } = this.props;
    const { country } = this.props.BirthPlace ?? {};
    const showForeignBornDocumentation = country && countryString(country) !== 'United States';
    const enteredCivilUnionMinDate = pickDate([this.props.applicantBirthdate, this.props.Birthdate]);
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="civil-union">
        <div>
          <Field
            title={i18n.t('relationships.pvq.civilUnion.para.name')}
            optional
            filterErrors={Name.requiredErrorsOnly}
            scrollIntoView={this.props.scrollIntoView}
          >
            <PVQName
              attribute={`${attribute as string}.Name`}
              name="name"
              className="civil"
              aria-label="Provide this person's full name"
              {...this.props.Name}
              onUpdate={this.updateName}
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
          <Field
            attribute={`${attribute}.Birthdate`}
            help="relationships.civilUnion.help.birthdate"
            title={i18n.t('relationships.pvq.civilUnion.para.birthdate')}
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="datecontrol"
          >
            <DateControl
              attribute={`${attribute}.Birthdate`}
              name="birthdate"
              className="birthdate"
              {...this.props.Birthdate}
              relationship="Other"
              onUpdate={(values: RelationshipsCivilUnionModel['Birthdate']) => this.update({
                Birthdate: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.BirthPlace`}
            title={i18n.t('relationships.pvq.civilUnion.heading.birthplace')}
            scrollIntoView={this.props.scrollIntoView}
          >
            <LocationUnknown
              name="birthplace"
              attribute={`${attribute}.BirthPlace`}
              {...this.props.BirthPlace}
              layout={Location.IDENTIFICATION_BIRTH_PLACE}
              className="birthplace"
              label={i18n.t('relationships.pvq.civilUnion.label.birthplace')}
              stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
              cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
              countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
              countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
              onUpdate={this.updateBirthPlace}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.Citizenship`}
            title={i18n.t('relationships.pvq.civilUnion.heading.citizenship')}
            help="relationships.civilUnion.help.citizenship"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="country"
          >
            <Country
              attribute={`${attribute}.Citizenship`}
              name="Citizenship"
              {...this.props.Citizenship}
              className="relationships-civilUnion-citizenship"
              multiple
              onUpdate={(values: RelationshipsCivilUnionModel['Citizenship']) => this.update({
                Citizenship: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
            {/* DROPDOWN */}
          </Field>

          <ForeignBornDocumentsCivil
            attribute={`${attribute}.ForeignBornDocumentCivil`}
            name="foreignBornDocumentCivil"
            {...this.props.ForeignBornDocumentCivil}
            onUpdate={this.updateForeignBornDocumentCivil}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            requireExpirationDate={requireRelationshipMaritalForeignBornDocExpiration}
            formType={this.props.formType}
          />

          <Field
            attribute={`${attribute}.SSN`}
            title={i18n.t('relationships.pvq.civilUnion.heading.ssn')}
            help="identification.ssn.help"
            scrollIntoView={this.props.scrollIntoView}
          >
            <SSN
              attribute={`${attribute}.SSN`}
              name="ssn"
              {...this.props.SSN}
              onUpdate={(values: RelationshipsCivilUnionModel['SSN']) => this.update({
                SSN: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <BranchCollection
            attribute={`${attribute}.OtherNames`}
            label={i18n.t('relationships.pvq.civilUnion.heading.othernames')}
            className="othername"
            appendLabel={i18n.t('relationships.pvq.civilUnion.heading.appendOthernames')}
            {...this.props.OtherNames}
            onError={this.props.onError}
            onUpdate={(values: RelationshipsCivilUnionModel['OtherNames']) => this.update({
              OtherNames: values,
            })}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showDNK
          >
            <OtherName
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              applicantMinDate={extractDate(this.props.Birthdate)}
              forceMinDate
              prefix="civilUnion"
              attribute={`${attribute}.OtherNames`}
              nameAttribute="Name"
              formType={this.props.formType}
            />
          </BranchCollection>
          {(this.props.maritalStatus === 'Married' || this.props.maritalStatus === 'Separated') && (
            <div>
              <Field
                attribute={`${attribute}.EnteredCivilUnion`}
                title={i18n.t('relationships.pvq.civilUnion.heading.enteredCivilUnion')}
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="datecontrol"
              >
                <DateControl
                  attribute={`${attribute}.EnteredCivilUnion`}
                  name="enteredCivilUnion"
                  className="entered"
                  minDateEqualTo
                  prefix="civilUnion"
                  minDate={enteredCivilUnionMinDate}
                  {...this.props.EnteredCivilUnion}
                  onUpdate={(values: RelationshipsCivilUnionModel['EnteredCivilUnion']) => this.update({
                    EnteredCivilUnion: values,
                  })}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
              <Field
                title={i18n.t('relationships.pvq.civilUnion.heading.locationCivilUnion')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Location`}
              >
                <LocationUnknown
                  attribute={`${attribute}.Location`}
                  name="Location"
                  {...this.props.Location}
                  layout={Location.IDENTIFICATION_BIRTH_PLACE}
                  stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
                  cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
                  countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
                  countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
                  onUpdate={(values: RelationshipsCivilUnionModel['Location']) => this.update({
                    Location: values,
                  })} // eslint-disable-line @typescript-eslint/unbound-method
                  onError={this.handleError}
                  required={this.props.required}
                />
              </Field>
            </div>
          )}
          {this.props.maritalStatus === 'Spouse' && (
            <div>
              <Field
                attribute={`${attribute}.EnteredCivilUnion`}
                title="When did you enter this relationship?"
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="datecontrol"
              >
                <DateControl
                  attribute={`${attribute}.EnteredCivilUnion`}
                  name="enteredCivilUnion"
                  className="entered"
                  minDateEqualTo
                  prefix="civilUnion"
                  minDate={enteredCivilUnionMinDate}
                  {...this.props.EnteredCivilUnion}
                  onUpdate={(values: RelationshipsCivilUnionModel['EnteredCivilUnion']) => this.update({
                    EnteredCivilUnion: values,
                  })}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </div>
          )}
          <Branch
            attribute={`${attribute}.SameAddress`}
            name="sameAddress"
            className="same-address"
            label={i18n.t('relationships.pvq.civilUnion.heading.sameLocation')}
            labelSize="h4"
            {...this.props.SameAddress}
            onUpdate={(values: RelationshipsCivilUnionModel['SameAddress']) => this.update({
              SameAddress: values,
            })}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
          {this.props.SameAddress?.value === 'No' && (
            <div>
              <Field
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.SpouseAddress`}
              >
                <LocationUnknown
                  attribute={`${attribute}.SpouseAddress`}
                  name="Address"
                  {...this.props.SpouseAddress}
                  layout={Location.ADDRESS}
                  label="Do they live in the U.S.?"
                  stateLabel={i18n.t('identification.pvq.birthplace.label.state')}
                  cityLabel={i18n.t('identification.pvq.birthplace.label.city')}
                  countyLabel={i18n.t('identification.pvq.birthplace.label.county')}
                  countryLabel={i18n.t('identification.pvq.birthplace.label.country')}
                  onUpdate={(values: RelationshipsCivilUnionModel['SpouseAddress']) => this.update({
                    SpouseAddress: values,
                  })} // eslint-disable-line @typescript-eslint/unbound-method
                  onError={this.handleError}
                  required={this.props.required}
                  showMilitaryInstallation
                  showApoFpoDpoPostcodeWhenForeign
                  hideInstallationNameUnknownWhenForeign
                />
              </Field>
            </div>
          )}
          <Field
            attribute={`${attribute}.Telephone`}
            title={i18n.t('relationships.pvq.civilUnion.heading.telephone')}
            className="override-required"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="telephone"
          >
            <NotApplicable
              attribute={`${attribute}.UseApplicantPhoneNotApplicable`}
              name="UseApplicantPhoneNotApplicable"
              className="applicant-phone-notapplicable"
              {...this.props.UseApplicantPhoneNotApplicable}
              label={i18n.t('relationships.pvq.civilUnion.useApplicantPhoneNotApplicable.idk')}
              or={i18n.m('relationships.civilUnion.useApplicantPhoneNotApplicable.or')}
              onError={this.props.onError}
              onUpdate={this.updateUseApplicantPhoneNotApplicable}
            >
              <Telephone
                attribute={`${attribute}.Telephone`}
                name="Telephone"
                className={
                  (this.props.UseApplicantPhoneNotApplicable || {}).applicable === true
                    ? 'telephone-number-fields'
                    : 'disabled'
                }
                {...this.props.Telephone}
                onUpdate={(values: RelationshipsCivilUnionModel['Telephone']) => this.update({ Telephone: values })}
                onError={this.props.onError}
                required={
                  (this.props.UseApplicantPhoneNotApplicable || {}).applicable
                  && this.props.required
                }
                allowNotApplicable={false}
                noNumber={(this.props.UseApplicantPhoneNotApplicable || {}).applicable === false}
                showNumberType
              />
            </NotApplicable>
          </Field>

          <Field
            attribute={`${attribute}.Email`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <NotApplicable
              attribute={setAttribute(this.props.attribute, 'EmailNotApplicable')}
              name="EmailNotApplicable"
              className="email-notapplicable"
              {...this.props.EmailNotApplicable}
              label={i18n.t('relationships.people.person.label.idk')}
              or={i18n.m('relationships.people.person.label.or')}
              onError={this.props.onError}
              onUpdate={(values: RelationshipsCivilUnionModel['EmailNotApplicable']) => this.update({
                Email: {},
                EmailNotApplicable: values,
              })}
            >
              <PVQEmail
                attribute={`${attribute}.Email`}
                name="Email"
                {...this.props.Email}
                emailFieldTitle={i18n.t('relationships.pvq.civilUnion.heading.email')}
                onUpdate={(values: RelationshipsCivilUnionModel['Email']) => this.update({
                  Email: values,
                })}
                onError={this.props.onError}
                required={(this.props.EmailNotApplicable || {}).applicable && this.props.required}
              />
            </NotApplicable>
          </Field>
          {this.props.maritalStatus === 'Separated'
            && (
              <div>
                <Field
                  attribute={`${attribute}.DateSeparated`}
                  title={i18n.t('relationships.pvq.civilUnion.heading.dateSeparated')}
                  help="relationships.civilUnion.help.dateSeparated"
                  scrollIntoView={this.props.scrollIntoView}
                  adjustFor="datecontrol"
                >
                  <DateControl
                    attribute={`${attribute}.DateSeparated`}
                    name="DateSeparated"
                    className="dateseparated"
                    minDate={this.props.EnteredCivilUnion}
                    minDateEqualTo
                    {...this.props.DateSeparated}
                    onUpdate={(values: RelationshipsCivilUnionModel['DateSeparated']) => this.update({
                      DateSeparated: values,
                    })}
                    onError={this.props.onError}
                    required={this.props.required}
                  />
                </Field>

                <Field
                  attribute={`${attribute}.GovOffice`}
                  shrink
                  title={i18n.t('relationships.pvq.civilUnion.heading.govName')}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <Text
                    attribute={`${attribute}.GovOffice`}
                    name="GovOffice"
                    {...this.props.GovOffice}
                    className="gov-office"
                    onUpdate={this.updateGovOffice}
                    onError={this.props.onError}
                    required={this.props.required}
                  />
                </Field>

                <Field
                  attribute={`${attribute}.AddressSeparated`}
                  className="address-separated"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <LocationUnknown
                    attribute={`${attribute}.AddressSeparated`}
                    name="addressSeparated"
                    {...this.props.AddressSeparated}
                    // Where are they recorded?
                    domesticTitle={i18n.t('relationships.pvq.civilUnion.heading.where')}
                    // Where are they recorded?
                    foreignTitle={i18n.t('relationships.pvq.civilUnion.heading.where')}
                    // Are the legal documents for your separation recorded in the U.S.?
                    label={i18n.t('relationships.pvq.civilUnion.heading.addressSeparated')}
                    layout={Location.ADDRESS}
                    showPostOffice
                    onUpdate={(values: RelationshipsCivilUnionModel['AddressSeparated']) => this.update({
                      AddressSeparated: values,
                    })}
                    onError={this.props.onError}
                    required={this.props.required}
                  />
                </Field>
              </div>
            )}
          <Branch
            attribute={`${attribute}.Divorced`}
            name="divorced"
            label={i18n.t('relationships.pvq.civilUnion.heading.divorced')}
            labelSize="h4"
            className="divorced"
            {...this.props.Divorced}
            onUpdate={this.updateDivorced}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
        </div>
      </div>
    ) : (
      <div className="civil-union">
        <div>
          <p>{i18n.t('relationships.civilUnion.para.never')}</p>

          <Field
            title={i18n.t('relationships.civilUnion.heading.name')}
            optional
            filterErrors={Name.requiredErrorsOnly}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Name
              attribute={`${attribute}.Name`}
              name="Name"
              className="civil"
              aria-label="Provide this person's full name"
              {...this.props.Name}
              onUpdate={this.updateName}
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
          <Field
            attribute={`${attribute}.Birthdate`}
            help="relationships.civilUnion.help.birthdate"
            title={i18n.t('relationships.civilUnion.heading.birthdate')}
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="datecontrol"
          >
            <DateControl
              attribute={`${attribute}.Birthdate`}
              name="birthdate"
              className="birthdate"
              {...this.props.Birthdate}
              relationship="Other"
              onUpdate={(values: RelationshipsCivilUnionModel['Birthdate']) => this.update({
                Birthdate: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.BirthPlace`}
            title={i18n.t('relationships.civilUnion.heading.birthplace')}
            scrollIntoView={this.props.scrollIntoView}
          >
            <LocationUnknown
              name="birthplace"
              attribute={`${attribute}.BirthPlace`}
              {...this.props.BirthPlace}
              layout={Location.BIRTHPLACE}
              className="birthplace"
              label={i18n.t('relationships.civilUnion.label.birthplace')}
              onUpdate={this.updateBirthPlace}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          <Comments
            title={i18n.t('relationships.comments.optional')}
            name={`${attribute}Comments`}
            className="optional-comment"
            attribute={`${attribute}Comments`}
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />

          <Show when={showForeignBornDocumentation}>
            <ForeignBornDocuments
              attribute={`${attribute}.ForeignBornDocument`}
              name="foreignBornDocument"
              {...this.props.ForeignBornDocument}
              onUpdate={this.updateForeignBornDocument}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              requireExpirationDate={requireRelationshipMaritalForeignBornDocExpiration}
              formType={this.props.formType}
            />
          </Show>

          <Field
            attribute={`${attribute}.SSN`}
            title={i18n.t('relationships.civilUnion.heading.ssn')}
            help="identification.ssn.help"
            scrollIntoView={this.props.scrollIntoView}
          >
            <SSN
              attribute={`${attribute}.SSN`}
              name="ssn"
              {...this.props.SSN}
              onUpdate={(values: RelationshipsCivilUnionModel['SSN']) => this.update({
                SSN: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <BranchCollection
            attribute={`${attribute}.OtherNames`}
            label={i18n.t('relationships.civilUnion.heading.othernames')}
            className="othername"
            appendLabel={i18n.t('relationships.civilUnion.heading.appendOthernames')}
            {...this.props.OtherNames}
            onError={this.props.onError}
            onUpdate={(values: RelationshipsCivilUnionModel['OtherNames']) => this.update({
              OtherNames: values,
            })}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <OtherName
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              applicantMinDate={extractDate(this.props.Birthdate)}
              forceMinDate
              prefix="civilUnion"
              attribute={`${attribute}.OtherNames`}
              nameAttribute="Name"
            />
          </BranchCollection>

          <Field
            attribute={`${attribute}.Citizenship`}
            title={i18n.t('relationships.civilUnion.heading.citizenship')}
            help="relationships.civilUnion.help.citizenship"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="country"
          >
            <Country
              attribute={`${attribute}.Citizenship`}
              name="Citizenship"
              {...this.props.Citizenship}
              className="relationships-civilUnion-citizenship"
              multiple
              onUpdate={(values: RelationshipsCivilUnionModel['Citizenship']) => this.update({
                Citizenship: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.EnteredCivilUnion`}
            title={i18n.t('relationships.civilUnion.heading.enteredCivilUnion')}
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="datecontrol"
          >
            <DateControl
              attribute={`${attribute}.EnteredCivilUnion`}
              name="enteredCivilUnion"
              className="entered"
              minDateEqualTo
              prefix="civilUnion"
              minDate={enteredCivilUnionMinDate}
              {...this.props.EnteredCivilUnion}
              onUpdate={(values: RelationshipsCivilUnionModel['EnteredCivilUnion']) => this.update({
                EnteredCivilUnion: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.Location`}
            title={i18n.t('relationships.civilUnion.heading.location')}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Location
              attribute={`${attribute}.Location`}
              name="Location"
              className="civilunion-location"
              {...this.props.Location}
              layout={Location.US_CITY_STATE_COUNTY_INTERNATIONAL_CITY_COUNTRY}
              label={i18n.t('relationships.civilUnion.label.location')}
              onUpdate={(values: RelationshipsCivilUnionModel['Location']) => this.update({
                Location: values,
              })}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.Address`}
            title={i18n.t(
              `relationships.civilUnion.heading.address${this.props.currentAddress ? '' : 'WithoutCurrent'
              }`
            )}
            optional
            help="relationships.civilUnion.help.address"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="address"
          >
            <NotApplicable
              attribute={`${attribute}.UseCurrentAddress`}
              name="UseCurrentAddress"
              applicable={!this.props.UseCurrentAddress?.applicable}
              className="current-address"
              label={i18n.t('relationships.civilUnion.useCurrentAddress.label')}
              or={i18n.m('relationships.civilUnion.useCurrentAddress.or')}
              onUpdate={this.updateUseCurrentAddress}
              onError={this.props.onError}
            >
              <Location
                attribute={`${attribute}.Address`}
                name="Address"
                {...this.props.Address}
                layout={Location.ADDRESS}
                geocode
                addressBooks={this.props.addressBooks}
                addressBook="Relative"
                showPostOffice
                dispatch={this.props.dispatch}
                onUpdate={(values: RelationshipsCivilUnionModel['Address']) => {
                  this.update({
                    Address: values,
                  });
                }}
                onError={this.props.onError}
                required={this.props.required}
                showDisabledText
              />
            </NotApplicable>
            <Show when={!this.props.UseCurrentAddress?.applicable}>
              <ConnectedAlternateAddress
                attribute={`${attribute}.AlternateAddress`}
                address={this.props.AlternateAddress}
                addressBook="Relative"
                belongingTo="AlternateAddress"
                country={this.props.Address?.country}
                militaryAddressLabel={i18n.t('address.militaryAddress.spouse')}
                onUpdate={this.update}
                AnswerComments={this.props.AnswerComments}
                AddressComments={this.props.AddressComments}
                PhysicalLocationComments={this.props.PhysicalLocationComments}
                foreignMilitaryAddressNegativeMargin={false}
              />
            </Show>
          </Field>

          <Field
            attribute={`${attribute}.Telephone`}
            title={i18n.t('relationships.civilUnion.heading.telephone')}
            className="override-required"
            scrollIntoView={this.props.scrollIntoView}
            adjustFor="telephone"
          >
            <NotApplicable
              attribute={`${attribute}.UseApplicantPhoneNotApplicable`}
              name="UseApplicantPhoneNotApplicable"
              className="applicant-phone-notapplicable"
              {...this.props.UseApplicantPhoneNotApplicable}
              label={i18n.t('relationships.civilUnion.useApplicantPhoneNotApplicable.label')}
              or={i18n.m('relationships.civilUnion.useApplicantPhoneNotApplicable.or')}
              onError={this.props.onError}
              onUpdate={this.updateUseApplicantPhoneNotApplicable}
            >
              <Telephone
                attribute={`${attribute}.Telephone`}
                name="Telephone"
                className={
                  (this.props.UseApplicantPhoneNotApplicable || {}).applicable === true
                    ? 'telephone-number-fields'
                    : 'disabled'
                }
                {...this.props.Telephone}
                onUpdate={(values: RelationshipsCivilUnionModel['Telephone']) => this.update({ Telephone: values })}
                onError={this.props.onError}
                required={
                  (this.props.UseApplicantPhoneNotApplicable || {}).applicable
                  && this.props.required
                }
                allowNotApplicable={false}
                noNumber={(this.props.UseApplicantPhoneNotApplicable || {}).applicable === false}
              />
            </NotApplicable>
          </Field>

          <Field
            attribute={`${attribute}.Email`}
            title={i18n.t('relationships.civilUnion.heading.email')}
            scrollIntoView={this.props.scrollIntoView}
          >
            <NotApplicable
              attribute={setAttribute(this.props.attribute, 'EmailNotApplicable')}
              name="EmailNotApplicable"
              className="email-notapplicable"
              {...this.props.EmailNotApplicable}
              label={i18n.t('relationships.people.person.label.idk')}
              or={i18n.m('relationships.people.person.label.or')}
              onError={this.props.onError}
              onUpdate={(values: RelationshipsCivilUnionModel['EmailNotApplicable']) => this.update({
                Email: {},
                EmailNotApplicable: values,
              })}
            >
              <Email
                attribute={`${attribute}.Email`}
                name="Email"
                {...this.props.Email}
                onUpdate={(values: RelationshipsCivilUnionModel['Email']) => this.update({
                  Email: values,
                })}
                onError={this.props.onError}
                required={(this.props.EmailNotApplicable || {}).applicable && this.props.required}
              />
            </NotApplicable>
          </Field>
          <Comments
            title={i18n.t('relationships.comments.optional')}
            name={`${attribute}ContinueComments`}
            className="optional-comment"
            attribute={`${attribute}ContinueComments`}
            {...this.props.ContinueComments}
            onUpdate={this.updateContinueComments}
            required={false}
          />
          <Branch
            attribute={`${attribute}.Separated`}
            name="separated"
            className="separated"
            label={i18n.t('relationships.civilUnion.heading.separated')}
            labelSize="h4"
            {...this.props.Separated}
            onUpdate={(values: RelationshipsCivilUnionModel['Separated']) => this.update({
              Separated: values,
            })}
            minDate={this.props.EnteredCivilUnion}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
          <Comments
            title={i18n.t('relationships.comments.optional')}
            name={`${attribute}SeparatedComments`}
            className="optional-comment"
            attribute={`${attribute}SeparatedComments`}
            {...this.props.SeparatedComments}
            onUpdate={this.updateSeparatedComments}
            required={false}
          />
          <Show when={((this.props.Separated || {}).value === 'Yes')}>
            <div>
              <Field
                attribute={`${attribute}.DateSeparated`}
                title={i18n.t('relationships.civilUnion.heading.dateSeparated')}
                help="relationships.civilUnion.help.dateSeparated"
                scrollIntoView={this.props.scrollIntoView}
                adjustFor="datecontrol"
              >
                <DateControl
                  attribute={`${attribute}.DateSeparated`}
                  name="DateSeparated"
                  className="dateseparated"
                  minDate={this.props.EnteredCivilUnion}
                  minDateEqualTo
                  {...this.props.DateSeparated}
                  onUpdate={(values: RelationshipsCivilUnionModel['DateSeparated']) => this.update({
                    DateSeparated: values,
                  })}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>

              <Field
                attribute={`${attribute}.AddressSeparated`}
                title={i18n.t('relationships.civilUnion.heading.addressSeparated')}
                className="address-separated"
                scrollIntoView={this.props.scrollIntoView}
              >
                <NotApplicable
                  attribute={`${attribute}.AddressSeparatedNotApplicable`}
                  name="AddressSeparatedNotApplicable"
                  {...this.props.AddressSeparatedNotApplicable}
                  label={i18n.t('relationships.civilUnion.notApplicable.label')}
                  or={i18n.m('relationships.civilUnion.notApplicable.or')}
                  onUpdate={(
                    values: RelationshipsCivilUnionModel['AddressSeparatedNotApplicable']
                  ) => this.update({
                    AddressSeparated: values.applicable ? this.props.AddressSeparated : {},
                    AddressSeparatedNotApplicable: values,
                  })}
                  onError={this.props.onError}
                >
                  <Location
                    attribute={`${attribute}.AddressSeparated`}
                    name="addressSeparated"
                    {...this.props.AddressSeparated}
                    label={i18n.t('relationships.civilUnion.label.addressSeparated')}
                    layout={Location.US_CITY_STATE_ZIP_INTERNATIONAL_CITY}
                    showPostOffice
                    onUpdate={(values: RelationshipsCivilUnionModel['AddressSeparated']) => this.update({
                      AddressSeparated: values,
                    })}
                    onError={this.props.onError}
                    required={this.props.required}
                  />
                </NotApplicable>
              </Field>
              <Comments
                title={i18n.t('relationships.comments.optional')}
                name={`${attribute}SeparationComments`}
                className="optional-comment"
                attribute={`${attribute}SeparationComments`}
                {...this.props.SeparationComments}
                onUpdate={this.updateSeparationComments}
                required={false}
              />
            </div>
          </Show>

          <Branch
            attribute={`${attribute}.Divorced`}
            name="divorced"
            label={i18n.t('relationships.civilUnion.heading.divorced')}
            labelSize="h4"
            className="divorced"
            {...this.props.Divorced}
            onUpdate={(values: RelationshipsCivilUnionModel['Divorced']) => this.update({
              Divorced: values,
            })}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
        </div>
        {this.props.divorcedComment?.()}
      </div>
    );
  }
}

CivilUnion.defaultProps = {
  Name: {},
  Birthdate: {},
  BirthPlace: {},
  ForeignBornDocument: {},
  ForeignBornDocumentCivil: {}, // PVQ ONLY
  SSN: {},
  OtherNames: {},
  Citizenship: {},
  EnteredCivilUnion: {},
  Address: {},
  SameAddress: {}, // PVQ ONLY
  SpouseAddress: {}, // PVQ ONLY
  Location: {},
  Telephone: {},
  Email: {},
  EmailNotApplicable: { applicable: true },
  Separated: {},
  DateSeparated: {},
  AddressSeparated: {},
  AddressSeparatedNotApplicable: {
    applicable: true,
  },
  GovOffice: {}, // PVQ ONLY
  Divorced: {},
  UseApplicantPhoneNotApplicable: { applicable: true },
  UseCurrentAddress: {},
  Comments: {},
  ContinueComments: {},
  SeparatedComments: {},
  SeparationComments: {},
  AnswerComments: {},
  AddressComments: {},
  PhysicalLocationComments: {},
  addressBooks: {},
  divorcedComment: () => { },
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr) => arr,
  defaultState: true,
  requireRelationshipMaritalForeignBornDocExpiration: true,
};

export default CivilUnion;
